package com.evoapps.evoshift.ui.billing

import com.evoapps.evoshift.ui.util.dayAndDateInSentence
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.TopBarAction
import com.evoapps.evoshift.widgets.helpAction
import com.evoapps.evoshift.ui.notifications.notificationsAction
import com.evoapps.evoshift.ui.util.HelpCentreButton
import com.evoapps.evoshift.ui.util.HelpPage
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import com.evoapps.evoshift.ui.adaptive.hoverLift
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.material.icons.filled.Restore
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material.icons.filled.WorkspacePremium
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.billing.OwnerPlan
import com.evoapps.evoshift.domain.billing.PlanResource
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.SubscriptionUiState
import com.evoapps.evoshift.viewmodels.SubscriptionViewModel
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.back
import evoshift.composeapp.generated.resources.paywall_limit_seats
import evoshift.composeapp.generated.resources.subscription_change_seats
import evoshift.composeapp.generated.resources.subscription_seats_used
import evoshift.composeapp.generated.resources.paywall_limit_workplaces
import evoshift.composeapp.generated.resources.paywall_restore
import evoshift.composeapp.generated.resources.paywall_terms
import evoshift.composeapp.generated.resources.subscription_billing_issue
import evoshift.composeapp.generated.resources.subscription_current_plan
import evoshift.composeapp.generated.resources.subscription_ends_on
import evoshift.composeapp.generated.resources.subscription_none_desc
import evoshift.composeapp.generated.resources.subscription_trial_desc
import evoshift.composeapp.generated.resources.subscription_trial_not_started
import evoshift.composeapp.generated.resources.subscription_manage
import evoshift.composeapp.generated.resources.subscription_never_expires
import evoshift.composeapp.generated.resources.subscription_owner_note
import evoshift.composeapp.generated.resources.subscription_renews_on
import evoshift.composeapp.generated.resources.subscription_sandbox
import evoshift.composeapp.generated.resources.subscription_title
import evoshift.composeapp.generated.resources.subscription_upgrade
import evoshift.composeapp.generated.resources.paywall_compare_title
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

/**
 * The owner's plan and subscription (SH-38), reached from the profile; EVoLabels'
 * SubscriptionStatusScreen in EVoShift's colours. A paid plan is managed in its store, and its
 * seats changed on the paywall; the trial, or no plan after it, leads to the paywall.
 */
@Composable
fun SubscriptionScreen(
    viewModel: SubscriptionViewModel,
    onNavigateBack: () -> Unit,
    onUpgrade: () -> Unit
) {
    val state by viewModel.state.collectAsState()
    SubscriptionContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onUpgrade = onUpgrade,
        onManage = viewModel::manage,
        onRestore = viewModel::restore,
        onMessageShown = viewModel::messageShown
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SubscriptionContent(
    state: SubscriptionUiState,
    onNavigateBack: () -> Unit,
    onUpgrade: () -> Unit,
    onManage: () -> Unit,
    onRestore: () -> Unit,
    onMessageShown: () -> Unit = {}
) {
    val snackbar = remember { SnackbarHostState() }
    val text = (state.error ?: state.message)?.asString()
    LaunchedEffect(text) {
        if (text != null) {
            snackbar.showSnackbar(text)
            onMessageShown()
        }
    }
    Scaffold(
        snackbarHost = { SnackbarHost(snackbar) },
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.subscription_title),
                onBack = onNavigateBack,
                actions = listOf(helpAction(HelpPage.PLANS))
            )
        }
    ) { padding ->
        val plan = state.plan
        if (plan == null) {
            Box(Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = DarkGold)
            }
            return@Scaffold
        }
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .contentWidth(ContentMaxWidth)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            PlanSummaryCard(plan)
            if (plan.entitlement?.hasBillingIssue == true) {
                Surface(color = MaterialTheme.colorScheme.errorContainer, shape = RoundedCornerShape(12.dp)) {
                    Row(Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Warning, null, tint = MaterialTheme.colorScheme.onErrorContainer)
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.subscription_billing_issue), color = MaterialTheme.colorScheme.onErrorContainer)
                    }
                }
            }
            RegularCard(Modifier.fillMaxWidth()) {
                Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(stringResource(Res.string.paywall_compare_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                    LimitRow(
                        stringResource(Res.string.paywall_limit_seats),
                        state.seatsInUse?.let { stringResource(Res.string.subscription_seats_used, it.toString(), limitLabel(plan.limits.maxSeats)) }
                            ?: limitLabel(plan.limits.maxSeats)
                    )
                    LimitRow(stringResource(Res.string.paywall_limit_workplaces), limitLabel(plan.limits.of(PlanResource.WORKPLACES)))
                }
            }
            Text(
                stringResource(Res.string.subscription_owner_note),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            if (plan.isPaid) {
                Button(
                    onClick = onManage,
                    enabled = !state.isManaging && plan.isBillingConfigured,
                    modifier = Modifier.fillMaxWidth().heightIn(min = 48.dp)
                ) {
                    if (state.isManaging) {
                        CircularProgressIndicator(Modifier.size(20.dp), strokeWidth = 2.dp, color = LocalContentColor.current)
                    } else {
                        Icon(Icons.AutoMirrored.Filled.OpenInNew, null, Modifier.size(20.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.subscription_manage), fontWeight = FontWeight.Bold)
                    }
                }
                OutlinedButton(onClick = onUpgrade, modifier = Modifier.fillMaxWidth().heightIn(min = 44.dp)) {
                    Text(stringResource(Res.string.subscription_change_seats))
                }
            } else {
                GoldButton(stringResource(Res.string.subscription_upgrade), onUpgrade, Modifier.fillMaxWidth())
            }
            if (plan.isBillingConfigured) {
                OutlinedButton(
                    onClick = onRestore,
                    enabled = !state.isRestoring,
                    modifier = Modifier.fillMaxWidth().heightIn(min = 44.dp)
                ) {
                    if (state.isRestoring) {
                        CircularProgressIndicator(Modifier.size(18.dp), strokeWidth = 2.dp)
                    } else {
                        Icon(Icons.Default.Restore, null, Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.paywall_restore))
                    }
                }
            }
            // Apple 3.1.2 (SH-93): the auto-renewal terms, Terms of Use and Privacy Policy wherever
            // the subscription is bought or managed, not only on the paywall.
            Text(
                stringResource(Res.string.paywall_terms),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            com.evoapps.evoshift.ui.legal.LegalLinks()
        }
    }
}

@Composable
private fun PlanSummaryCard(plan: OwnerPlan) {
    StandoutCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Box(
                    Modifier.background(GoldMetallicBrush, RoundedCornerShape(8.dp)).padding(horizontal = 10.dp, vertical = 4.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.WorkspacePremium, null, tint = DeepNavy, modifier = Modifier.size(16.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(plan.tier.label().uppercase(), color = DeepNavy, fontWeight = FontWeight.Black, style = MaterialTheme.typography.labelMedium)
                    }
                }
                if (plan.entitlement?.isSandbox == true) {
                    Text(stringResource(Res.string.subscription_sandbox), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.labelSmall)
                }
            }
            Text(
                "${stringResource(Res.string.subscription_current_plan)}: ${plan.tier.label()}",
                style = MaterialTheme.typography.headlineSmall,
                color = LightGold,
                fontWeight = FontWeight.Bold
            )
            if (plan.isPaid) Text(seatsLabel(plan.seats), color = Color.White, fontWeight = FontWeight.SemiBold)
            val entitlement = plan.entitlement
            if (entitlement == null) {
                val trialEnds = plan.trialEndsAt
                val description = when {
                    plan.tier == PlanTier.NONE -> stringResource(Res.string.subscription_none_desc)
                    trialEnds == null -> stringResource(Res.string.subscription_trial_not_started, trialLengthLabel())
                    else -> stringResource(Res.string.subscription_trial_desc, dayAndMonth(trialEnds.toLocalDateTime(TimeZone.currentSystemDefault()).date))
                }
                Text(description, color = Color.White.copy(alpha = 0.85f))
            } else {
                val expires = entitlement.expiresAt
                val status = when {
                    expires == null -> stringResource(Res.string.subscription_never_expires)
                    else -> {
                        val date = dayAndDateInSentence(expires.toLocalDateTime(TimeZone.currentSystemDefault()).date)
                        stringResource(if (entitlement.willRenew) Res.string.subscription_renews_on else Res.string.subscription_ends_on, date)
                    }
                }
                Text(status, color = Color.White.copy(alpha = 0.9f))
                entitlement.store.label()?.let { Text(it, color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall) }
            }
        }
    }
}

/** The owner's plan in the profile (SH-38): opens [SubscriptionScreen]. */
@Composable
fun OwnerPlanEntry(viewModel: SubscriptionViewModel, onClick: () -> Unit) {
    val plan = viewModel.state.collectAsState().value.plan
    RegularCard(Modifier.fillMaxWidth().hoverLift(), onClick = onClick) {
        Row(Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.WorkspacePremium, null, tint = DarkGold)
            Spacer(Modifier.width(12.dp))
            Column(Modifier.weight(1f)) {
                Text(stringResource(Res.string.subscription_title), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                if (plan != null) {
                    Text(
                        "${stringResource(Res.string.subscription_current_plan)}: ${plan.tier.label()}",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null)
        }
    }
}

@Composable
private fun LimitRow(label: String, value: String) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label)
        Text(value, fontWeight = FontWeight.Bold)
    }
}
